import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { joinVillaWaitlist, formatApiError } from "@/lib/api";

const VILLA_OPTIONS = ["Any Villa", "Villa 1", "Villa 2", "Villa 3", "Villa 4", "Villa 5", "Villa 6"];

export default function VillaWaitlistForm() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [preferred, setPreferred] = useState(VILLA_OPTIONS[0]);
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) {
      setError("Please provide your email.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await joinVillaWaitlist({
        email,
        name: name || undefined,
        preferred_villa: preferred,
      });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="villa-waitlist"
      className="mb-8 border border-[#D4AF37]/50 bg-gradient-to-br from-[#D4AF37]/[0.08] to-[#0F1F35] p-5 md:p-6 relative overflow-hidden"
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />

      <AnimatePresence mode="wait">
        {!result ? (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="relative"
          >
            <div className="flex items-start gap-3 mb-4">
              <Sparkles className="text-[#D4AF37] mt-1 shrink-0" size={18} />
              <div>
                <div className="overline mb-1">Pre-launch waitlist</div>
                <h4 className="font-serif-display text-2xl md:text-3xl text-[#F3EFE4] leading-tight">
                  Reserve a villa <span className="italic text-[#D4AF37]">before we open sales.</span>
                </h4>
                <p className="text-xs text-[#8FA3B8] mt-2 leading-relaxed max-w-md">
                  Get first-look pricing and priority selection when Reign Villa Resorts opens for reservations. No commitment — one email is all we need.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <input
                type="email"
                data-testid="waitlist-input-email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="field-input flex-1 !border-b-[#D4AF37]/40"
              />
              <button
                type="submit"
                data-testid="waitlist-submit"
                disabled={submitting}
                className="btn-primary !py-3 !px-6 whitespace-nowrap disabled:opacity-50"
              >
                {submitting ? "Joining..." : "Join Waitlist"}
                {!submitting && <span className="btn-arrow">→</span>}
              </button>
            </div>

            {!expanded ? (
              <button
                type="button"
                data-testid="waitlist-expand"
                onClick={() => setExpanded(true)}
                className="text-[0.7rem] tracking-[0.22em] uppercase text-[#8FA3B8] hover:text-[#D4AF37] transition mt-4"
              >
                + Add name & villa preference (optional)
              </button>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4 mt-5 pt-5 border-t border-white/10">
                <div>
                  <label className="field-label" htmlFor="wl-name">Full Name</label>
                  <input
                    id="wl-name"
                    data-testid="waitlist-input-name"
                    className="field-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="wl-villa">Preferred villa</label>
                  <select
                    id="wl-villa"
                    data-testid="waitlist-input-villa"
                    className="field-input"
                    value={preferred}
                    onChange={(e) => setPreferred(e.target.value)}
                  >
                    {VILLA_OPTIONS.map((v) => (
                      <option key={v} value={v} className="bg-[#0F1F35]">{v}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {error && (
              <div data-testid="waitlist-error" className="mt-4 text-xs text-red-400 border border-red-500/30 px-3 py-2">
                {error}
              </div>
            )}

            <p className="text-[10px] text-[#8FA3B8] mt-4">
              We'll only contact you about Reign Villa Resorts. Unsubscribe anytime.
            </p>
          </motion.form>
        ) : (
          <motion.div
            key="success"
            data-testid="waitlist-success"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative text-center py-6"
          >
            <div className="mx-auto mb-5 w-14 h-14 border border-[#D4AF37] flex items-center justify-center">
              <Check className="text-[#D4AF37]" size={26} />
            </div>
            <div className="overline mb-2">{result.already_on_list ? "Already on the list" : "You're in"}</div>
            {!result.already_on_list && (
              <div className="font-serif-display text-5xl md:text-6xl text-[#D4AF37] mb-2 leading-none">
                #{result.position}
              </div>
            )}
            <h4 className="font-serif-display text-2xl text-[#F3EFE4] mb-3">
              {result.already_on_list ? "Welcome back." : "On the waitlist."}
            </h4>
            <p className="text-sm text-[#8FA3B8] max-w-sm mx-auto leading-relaxed">{result.message}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
